import React, { useState } from "react";
import { RefreshCw, Download, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { T, primaryBtn, inputStyle, labelStyle, getEngagementVerdict, downloadBlob, fmtDateIT } from "../theme";
import { InfoHint, ItalianDateInput } from "./shared";

export default function TrendTab({ profile, staticData, actions }) {
  const [newDate, setNewDate] = useState("");
  const [newFollowers, setNewFollowers] = useState("");
  const [newEngagement, setNewEngagement] = useState("");
  const [competitorNameInput, setCompetitorNameInput] = useState("");
  const [compDate, setCompDate] = useState("");
  const [compFollowers, setCompFollowers] = useState("");

  const history = profile.history;
  const hasData = history.length > 0;
  const currentFollowers = history[history.length - 1]?.followers || 0;
  const currentEngagement = history[history.length - 1]?.engagement || 0;

  const growthProjection = (() => {
    if (history.length < 2) return null;
    const sorted = [...history].sort((a, b) => a.date.localeCompare(b.date));
    const first = sorted[0], last = sorted[sorted.length - 1];
    const days = (new Date(last.date) - new Date(first.date)) / 86400000;
    if (days < 3) return null;
    const dailyGrowth = (last.followers - first.followers) / days;
    return {
      dailyGrowth,
      in30: Math.max(0, Math.round(last.followers + dailyGrowth * 30)),
      in90: Math.max(0, Math.round(last.followers + dailyGrowth * 90)),
    };
  })();

  function exportCSV() {
    const rows = ["data,follower,engagement"];
    history.forEach((h) => rows.push(`${h.date},${h.followers},${h.engagement}`));
    downloadBlob(`${profile.name}-andamento.csv`, rows.join("\n"), "text/csv");
  }

  const competitor = profile.competitor;

  return (
    <div data-testid="trend-tab">
      {profile.instagram_connected && (
        <div style={{ background: T.surfaceAlt, border: `1px solid ${T.teal}`, borderRadius: 10, padding: 16, marginBottom: 16, maxWidth: 560, display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontFamily: T.fontHead, fontSize: 14, marginBottom: 4 }}>Instagram collegato</div>
            <div style={{ fontSize: 12, color: T.textDim }}>Sincronizza i tuoi numeri più recenti con un tocco, invece di inserirli a mano (sincronizzazione simulata in questa versione).</div>
          </div>
          <button data-testid="trend-sync-btn" onClick={actions.syncInstagram} style={{ ...primaryBtn, flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}><RefreshCw size={14} /> Sincronizza ora</button>
        </div>
      )}
      <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 16, marginBottom: 20, maxWidth: 560 }}>
        <div style={{ fontSize: 13, color: T.textDim, marginBottom: 12 }}>{profile.instagram_connected ? "Oppure inserisci un dato manualmente" : "Registra un nuovo dato"}</div>
        <div className="grid-form-3">
          <div><label style={labelStyle}>Data</label><ItalianDateInput testId="trend-date-input" value={newDate} onChange={setNewDate} /></div>
          <div><label style={labelStyle}>Follower</label><input data-testid="trend-followers-input" type="number" style={inputStyle} value={newFollowers} onChange={(e) => setNewFollowers(e.target.value)} placeholder="1450" /></div>
          <div><label style={labelStyle}>Engagement %<InfoHint text="La percentuale di chi interagisce (like, commenti, salvataggi) rispetto a chi vede i tuoi contenuti." /></label><input data-testid="trend-engagement-input" type="number" step="0.1" style={inputStyle} value={newEngagement} onChange={(e) => setNewEngagement(e.target.value)} placeholder="2.9" /></div>
          <button data-testid="trend-add-data-btn" onClick={() => { if (newDate && newFollowers) { actions.addDataPoint(newDate, parseInt(newFollowers, 10), parseFloat(newEngagement) || 0); setNewDate(""); setNewFollowers(""); setNewEngagement(""); } }} style={{ ...primaryBtn, height: 36 }}>Aggiungi</button>
        </div>
      </div>
      <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontSize: 13, color: T.textDim }}>Follower ed engagement nel tempo</div>
          {hasData && (
            <button data-testid="trend-export-csv-btn" onClick={exportCSV} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, borderRadius: 6, padding: "5px 10px", fontSize: 12, cursor: "pointer" }}><Download size={12} /> Esporta CSV</button>
          )}
        </div>
        {hasData ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={history}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
              <XAxis dataKey="date" stroke={T.textDim} fontSize={12} tickFormatter={fmtDateIT} />
              <YAxis yAxisId="left" stroke={T.textDim} fontSize={12} />
              <YAxis yAxisId="right" orientation="right" stroke={T.textDim} fontSize={12} />
              <Tooltip contentStyle={{ background: T.surface, border: `1px solid ${T.line}`, color: T.text }} labelFormatter={fmtDateIT} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="left" type="monotone" dataKey="followers" name="Follower" stroke={T.amber} strokeWidth={2} dot={{ r: 3 }} />
              <Line yAxisId="right" type="monotone" dataKey="engagement" name="Engagement %" stroke={T.teal} strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div data-testid="trend-empty-state" style={{ color: T.textDim, fontSize: 13, padding: "30px 0", textAlign: "center" }}>Aggiungi almeno un dato per vedere il grafico.</div>
        )}
      </div>

      {hasData && (() => {
        const verdict = getEngagementVerdict(staticData.benchmarks, currentFollowers, currentEngagement);
        return (
          <div data-testid="trend-benchmark-card" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 18, marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 13, color: T.textDim, display: "flex", alignItems: "center" }}>
                Il tuo engagement rispetto al settore<InfoHint text="Stime sintetizzate dagli studi 2026 più citati (Rival IQ, Socialinsider, HypeAuditor, SociaVault). I metodi di calcolo variano molto da uno studio all'altro: usa questo dato come orientamento generale, non come verdetto assoluto." />
              </div>
              <span data-testid="trend-benchmark-verdict" style={{ fontSize: 11, fontFamily: T.fontHead, color: T.ink, background: verdict.color, borderRadius: 20, padding: "3px 10px" }}>{verdict.label}</span>
            </div>
            <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.5, maxWidth: 560 }}>
              Il tuo engagement è <b style={{ color: T.text }}>{currentEngagement}%</b>, per un profilo {verdict.tierLabel}.
              Più un profilo cresce, più questo tasso scende fisiologicamente: non è un segnale di peggioramento, conta di più
              l'andamento nel tempo rispetto al tuo stesso passato che il confronto con un numero generico.
            </div>
          </div>
        );
      })()}

      {growthProjection && (
        <div data-testid="trend-projection-card" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 16, padding: 18, marginTop: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
            <TrendingUp size={15} color={T.teal} />
            <div style={{ fontSize: 13, color: T.textDim }}>Se continui con questo ritmo<InfoHint text="Una semplice proiezione lineare tra il tuo primo e ultimo dato registrato: non è una promessa, è quello che succederebbe se il ritmo attuale restasse invariato." /></div>
          </div>
          <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontFamily: T.fontHead, fontSize: 22, color: T.text }}>{growthProjection.in30.toLocaleString("it-IT")}</div>
              <div style={{ fontSize: 12, color: T.textDim }}>follower stimati tra 30 giorni</div>
            </div>
            <div>
              <div style={{ fontFamily: T.fontHead, fontSize: 22, color: T.text }}>{growthProjection.in90.toLocaleString("it-IT")}</div>
              <div style={{ fontSize: 12, color: T.textDim }}>follower stimati tra 90 giorni</div>
            </div>
          </div>
          {growthProjection.dailyGrowth <= 0 && (
            <div style={{ fontSize: 12, color: T.rose, marginTop: 10 }}>Il trend attuale è piatto o in calo: è il momento di seguire con più continuità il piano consigliato in "Crescita".</div>
          )}
        </div>
      )}

      <div data-testid="trend-competitor-card" style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 18, marginTop: 20 }}>
        <div style={{ fontSize: 13, color: T.textDim, marginBottom: 4 }}>Confronto con un concorrente di settore</div>
        <div style={{ fontSize: 12, color: T.textDim, marginBottom: 14, maxWidth: 500 }}>Inserisci a mano i follower di un profilo simile al tuo (pubblici, visibili a chiunque): ti aiuta a capire se stai crescendo più o meno velocemente di chi fa il tuo stesso lavoro.</div>

        {!competitor ? (
          <div style={{ display: "flex", gap: 10 }}>
            <input data-testid="competitor-name-input" style={inputStyle} value={competitorNameInput} onChange={(e) => setCompetitorNameInput(e.target.value)} placeholder="Nome del profilo concorrente (es. @nomeaccount)" />
            <button data-testid="competitor-add-btn" onClick={() => { if (competitorNameInput.trim()) { actions.setCompetitor(competitorNameInput.trim()); setCompetitorNameInput(""); } }} style={{ ...primaryBtn, flexShrink: 0 }}>Aggiungi</button>
          </div>
        ) : (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 13 }}>Confronto con <b>{competitor.name}</b></div>
              <button data-testid="competitor-remove-btn" onClick={actions.removeCompetitor} style={{ background: "transparent", border: "none", color: T.textDim, cursor: "pointer", fontSize: 12 }}>Rimuovi</button>
            </div>
            <div className="grid-form-comp" style={{ marginBottom: 16, maxWidth: 420 }}>
              <div><label style={labelStyle}>Data</label><ItalianDateInput testId="competitor-date-input" value={compDate} onChange={setCompDate} /></div>
              <div><label style={labelStyle}>Follower</label><input data-testid="competitor-followers-input" type="number" style={inputStyle} value={compFollowers} onChange={(e) => setCompFollowers(e.target.value)} placeholder="2100" /></div>
              <button data-testid="competitor-add-entry-btn" onClick={() => { if (compDate && compFollowers) { actions.addCompetitorPoint(compDate, parseInt(compFollowers, 10)); setCompDate(""); setCompFollowers(""); } }} style={{ ...primaryBtn, height: 36 }}>Aggiungi</button>
            </div>

            {history.length > 0 && (competitor.entries || []).length > 0 ? (
              (() => {
                const compHistory = competitor.entries;
                const dates = Array.from(new Set([...history.map((h) => h.date), ...compHistory.map((h) => h.date)])).sort();
                const merged = dates.map((date) => ({
                  date,
                  tu: history.find((h) => h.date === date)?.followers ?? null,
                  concorrente: compHistory.find((h) => h.date === date)?.followers ?? null,
                }));
                const tuGrowth = history.length > 1 ? history[history.length - 1].followers - history[0].followers : 0;
                const compGrowth = compHistory.length > 1 ? compHistory[compHistory.length - 1].followers - compHistory[0].followers : 0;
                return (
                  <>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={merged}>
                        <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
                        <XAxis dataKey="date" stroke={T.textDim} fontSize={12} tickFormatter={fmtDateIT} />
                        <YAxis stroke={T.textDim} fontSize={12} />
                        <Tooltip contentStyle={{ background: T.surface, border: `1px solid ${T.line}`, color: T.text }} labelFormatter={fmtDateIT} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Line type="monotone" dataKey="tu" name="Tu" stroke={T.amber} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                        <Line type="monotone" dataKey="concorrente" name={competitor.name} stroke={T.teal} strokeWidth={2} connectNulls dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                    <div data-testid="competitor-summary" style={{ fontSize: 13, color: T.textDim, marginTop: 10 }}>
                      {tuGrowth >= compGrowth
                        ? <>Nello stesso periodo stai crescendo più velocemente di {competitor.name} (+{tuGrowth} contro +{compGrowth} follower).</>
                        : <>Nello stesso periodo {competitor.name} sta crescendo più velocemente di te (+{compGrowth} contro +{tuGrowth} follower).</>}
                    </div>
                  </>
                );
              })()
            ) : (
              <div style={{ color: T.textDim, fontSize: 13, textAlign: "center", padding: "16px 0" }}>Aggiungi almeno un dato tuo e uno del concorrente per vedere il confronto.</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
